import { html } from 'lit';
import { MobxLitElement } from '@adobe/lit-mobx';
import { customElement, property, state } from 'lit/decorators.js';
import '@digital-realty/ix-icon-button/ix-icon-button.js';
import '@digital-realty/ix-icon/ix-icon.js';
import { formatDistance } from 'date-fns/formatDistance.js';
import { differenceInHours } from 'date-fns/differenceInHours.js';
import { TWStyles } from '../../tw.js';
import { NotificationsState } from '../../state/NotificationState.js';
import { ApiCallResult, Notification } from '../../models/notification.js';
import {
  NotificationStatus,
  NotificationGroups,
  SubGroups,
} from '../../constants/notifications.js';
import { ConfirmationDialogEventDetail } from '../../constants/notification-types.js';
import { NotificationsStyle } from '../../styles/notifications-style.js';
import './confirmation-dialog.js';
import './view-item-dialog.js';
import '@digital-realty/ix-toast/ix-message-toast.js';
import { dcim, plannedMaintenance, trash } from '../../assets/iconset.js';

@customElement('notification-item')
export class NotificationItem extends MobxLitElement {
  @property({ type: Boolean }) disabled = false;

  @property({ type: Boolean }) read = false;

  @property({ type: Object, attribute: false }) notification =
    {} as Notification;

  @state() private showModal = false;

  @state() private showDeleteConfirmation = false;

  static get styles() {
    return [TWStyles, NotificationsStyle];
  }

  displayItem() {
    if (this.notification.status === NotificationStatus.UNREAD) {
      NotificationsState.setNotificationReadStatus(this.notification.id);
      this.read = true;
    }
    this.showModal = true;
  }

  deleteItem() {
    if (!this.disabled) {
      this.showDeleteConfirmation = true;
    }
  }

  private confirmedDelete(e: CustomEvent<ConfirmationDialogEventDetail>) {
    // DELETE IF CONFIRMED TRUE
    if (e.detail.returnValue) {
      NotificationsState.setNotificationDeleted(this.notification.id).then(
        (result: ApiCallResult) => {
          this.showDeleteResultMessage(result, this.notification.id);
        }
      );
    }
    this.showDeleteConfirmation = false;
  }

  // eslint-disable-next-line class-methods-use-this
  private showDeleteResultMessage(result: ApiCallResult, id: string) {
    window.dispatchEvent(
      new CustomEvent('add-toast', {
        detail: {
          id,
          content: html` <ix-message-toast
            data-testid="notification-delete-toast"
            toastId=${id}
            .TMessageToast=${result.State.toLowerCase()}
            forceClose
          >
            ${result.Message}
          </ix-message-toast>`,
          autoClose: 2000,
          durationOut: 2000,
          vertical: 'bottom',
          horizontal: 'center',
          animated: true,
          above: false,
        },
      })
    );
  }

  calculateDuration() {
    if (this.notification.createdAt !== undefined) {
      if (
        differenceInHours(new Date(), new Date(this.notification.createdAt)) <
        24
      )
        return formatDistance(
          new Date(this.notification.createdAt),
          new Date(),
          {
            addSuffix: true,
          }
        );
      return html`<ix-date
        type="static"
        variant="date-time"
        .value=${this.notification.createdAt}
      ></ix-date>`;
    }
    return 'N/A';
  }

  getNotificationIcon() {
    switch (this.notification.resourceType) {
      case NotificationGroups.PLANNED_MAINTENANCE:
        return plannedMaintenance;
      case NotificationGroups.SERVICE_TICKETS:
        return 'sync_saved_locally';
      case NotificationGroups.INCIDENTS:
        return 'report';
      case NotificationGroups.DCIM_ALERTS:
        return dcim;
      default:
        return 'sync_saved_locally';
    }
  }

  getNotificationTitle() {
    switch (this.notification.resourceType) {
      case NotificationGroups.PLANNED_MAINTENANCE:
        return 'Planned Maintenance';
      case NotificationGroups.SERVICE_TICKETS:
        return `${this.getServiceTicketSubGroup()} Service Ticket Update`;
      case NotificationGroups.INCIDENTS:
        return 'Emergency Repair';
      case NotificationGroups.DCIM_ALERTS:
        return 'DCIM Alert';
      default:
        return 'sync_saved_locally';
    }
  }

  getServiceTicketSubGroup() {
    switch (this.notification?.subGroup) {
      case SubGroups.FacilityAccessVisitorAccess:
      case SubGroups.FacilityAccessPermanentBadge:
      case SubGroups.FacilityAccessOspAccess:
      case SubGroups.FacilityAccessPopPoeAccess:
        return 'Facility Access';
      default:
        return this.notification?.subGroup;
    }
  }

  /* eslint-disable lit-a11y/click-events-have-key-events */
  render() {
    return html`
      <div data-testid="notification-item" class="relative notification-item">
        <div
          class="hover:bg-[rgba(0,0,0,0.04)] p-2 pl-3.5 flex gap-3 relative cursor-pointer items-center font-bold ${this
            .read
            ? 'notification-item-read border-l-3 border-transparent'
            : 'notification-item-unread border-l-3 border-success'}"
        >
          <ix-icon
            style="--ix-icon-font-size: 24px;--ix-icon-line-height: 24px;"
            icon=${this.getNotificationIcon()}
            class=${this.read ? 'inactive-icon' : 'active-icon'}
            @click=${this.displayItem}
            >${this.getNotificationIcon()}
          </ix-icon>
          <div class="flex flex-col grow" @click=${this.displayItem}>
            <div class="grow text-base">
              <h2 class="dlr-text-caption">${this.getNotificationTitle()}</h2>
            </div>
            <div class="grow text-xs">
              <h3 class="dlr-text-caption text-on-surface-disabled">
                ${this.calculateDuration()}
              </h3>
            </div>
          </div>
          <div>
            <view-item-dialog
              ?open=${this.showModal}
              .notification=${this.notification}
              @view-item-dialog-closed=${() => {
                this.showModal = false;
              }}
            >
            </view-item-dialog>
            <confirmation-dialog
              ?open=${this.showDeleteConfirmation}
              @on-confirm-selection=${this.confirmedDelete}
              @confirm-dialog-closed=${() => {
                this.showModal = false;
              }}
              textMessage="Are you sure you want to delete this notification?"
              data-testclass="notification-delete-confirmation"
            >
            </confirmation-dialog>
          </div>
          <ix-icon-button
            style="--md-icon-button-icon-size:24px;"
            data-testclass="delete-notification"
            @click=${this.deleteItem}
            @mouseover=${(e: any) => {
              this.dispatchEvent(
                new CustomEvent('show-notification-tooltip', {
                  detail: {
                    label: this.disabled
                      ? 'Emergency Repair Notification cannot be removed'
                      : 'Remove Notification',
                    target: e.target,
                  },
                })
              );
            }}
            @mouseout=${() =>
              this.dispatchEvent(new CustomEvent('hide-notification-tooltip'))}
            ?disabled=${this.disabled}
          >
            <ix-icon
              slot="default"
              class="${this.disabled ? 'inactive-icon' : 'destructive-icon'}"
            >
              ${trash}
            </ix-icon>
          </ix-icon-button>
        </div>
      </div>
    `;
  }
}
